import { useMutation } from '@tanstack/react-query';

import type { IndeedJobContact } from './indeed.types';

import { browserExtensionClient } from 'clients/BrowserExtensionClient/BrowserExtensionClient';
import { logger } from 'config/logger';
import { queryClient } from 'config/queryClient';
import { recordNewRelicCustomEvent, truncateForTelemetry } from 'shared/lib/telemetry';
import { IndeedCandidateAlreadyContactedException } from 'shared/legacy/exceptions/IndeedCandidateAlreadyContactedException';
import { IndeedCandidateRecentlyDeclinedException } from 'shared/legacy/exceptions/IndeedCandidateRecentlyDeclinedException';
import { QueryKey } from 'types/query';
import { useCurrentUserQuery } from '../../useCurrentUserQuery';
import { useIndeedConfig } from './useIndeedConfig';

type TalentEngagementCreatedResult = {
  additionalInfo: null;
  candidateId: string;
  __typename: 'TalentEngagementCreatedResult';
};

type CreateTalentEngagementError = {
  candidateId: string;
  code: string;
  message: string;
  __typename: 'CreateTalentEngagementError';
};

type SendMessageResponse = {
  createTalentEngagements: {
    results: Array<TalentEngagementCreatedResult | CreateTalentEngagementError>;
    successCount: number;
    __typename: 'CreateTalentEngagementsPayload';
  };
};

type ProxyHttpResponse = {
  body: string;
  status: number;
};

type MessageData = {
  candidateId: string;
  jobTitle: string;
  message: string;
  senderName: string;
  senderCompany: string;
  projectKey: string;
  job?: IndeedJobContact | null;
};

type SendMessageHandler = (params: {
  headers: Record<string, any>;
  data: MessageData;
  tenantId?: number;
  userId?: number;
  userEmail?: string;
}) => Promise<SendMessageResponse>;

const logIndeedSendMessageResponse = ({
  response,
  candidateId,
  tenantId,
  userId,
  userEmail,
}: {
  response: ProxyHttpResponse;
  candidateId: string;
  tenantId?: number;
  userId?: number;
  userEmail?: string;
}) => {
  try {
    recordNewRelicCustomEvent('IndeedSendMessageResponse', {
      userId: userId ?? '',
      agentIdentifier: userId ?? '',
      userEmail: userEmail ?? '',
      rr_user_email: userEmail ?? '',
      candidateId,
      tenantId: tenantId ?? '',
      responseBody: truncateForTelemetry(response.body),
      statusCode: response.status,
      timestamp: new Date().toISOString(),
    });
  } catch (loggingError) {
    logger.error(loggingError, { context: 'IndeedSendMessageResponseLogging' });
  }
};

export const useIndeedSendMessageMutation = () => {
  const indeedConfig = useIndeedConfig();
  const { data: currentUser } = useCurrentUserQuery();

  return useMutation(
    async (data: MessageData) => {
      if (!indeedConfig.isLoggedIn) throw Error('[useIndeedSendMessageMutation] Indeed user not logged in!');

      const response = await sendMessage({
        data,
        headers: indeedConfig.headers,
        tenantId: currentUser?.tenant.id,
        userId: currentUser?.id,
        userEmail: currentUser?.email,
      });

      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKey.useIndeedCommonModelQuery], { exact: false });
      },
    },
  );
};

const createTalentEngagementsGQLMutation = ({
  candidateId,
  senderName,
  company,
  jobTitle,
  message,
  projectKey,
  job,
}: {
  candidateId: string;
  senderName: string;
  company: string;
  jobTitle: string;
  message: string;
  projectKey: string;
  job?: IndeedJobContact | null;
}) => ({
  operationName: 'CreateTalentEngagements',
  variables: {
    input: {
      candidateIds: [candidateId],
      detailsByType: {
        email: {
          message,
          company,
          senderName,
          jobInfo: {
            jobId: job?.key ?? '',
            jobDescription: job?.description ?? '',
            jobTitle: job?.name ?? jobTitle,
          },
          includeProfileSignature: true,
          reminders: [],
          subject: '',
        },
      },
      metadata: {
        editedAfterPreview: null,
        previewed: null,
        skipProactiveModerationCheck: false,
        source: 'RESUME',
        templateId: '',
      },
      projectKey,
    },
  },
  query:
    'mutation CreateTalentEngagements($input: CreateTalentEngagementsInput!) {\n  createTalentEngagements(input: $input) {\n    results {\n      ... on CreateTalentEngagementError {\n        candidateId\n        code\n        message\n        __typename\n      }\n      ... on TalentEngagementCreatedResult {\n        additionalInfo {\n          ... on CreateTalentEngagementForPhoneDetails {\n            proxyPhoneNumber\n            __typename\n          }\n          __typename\n        }\n        candidateId\n        __typename\n      }\n      __typename\n    }\n    successCount\n    __typename\n  }\n}\n',
});

export const sendMessage: SendMessageHandler = async ({ data, headers, tenantId, userId, userEmail }) => {
  const mutation = createTalentEngagementsGQLMutation({
    candidateId: data.candidateId,
    company: data.senderCompany,
    jobTitle: data.jobTitle,
    message: data.message,
    senderName: data.senderName,
    projectKey: data.projectKey,
    job: data.job,
  });

  const response = await browserExtensionClient.sendMessage<ProxyHttpResponse>('proxyHttpRequest', {
    url: 'https://apis.indeed.com/graphql',
    method: 'POST',
    body: JSON.stringify(mutation),
    headers,
  });

  logIndeedSendMessageResponse({
    response,
    candidateId: data.candidateId,
    tenantId,
    userId,
    userEmail,
  });

  const parsedResponse: SendMessageResponse = JSON.parse(response.body).data;

  parsedResponse.createTalentEngagements.results.forEach((el) => {
    if (el.__typename === 'CreateTalentEngagementError' && el.code === 'ALREADY_CONTACTED') {
      throw new IndeedCandidateAlreadyContactedException(el.message);
    }

    if (el.__typename === 'CreateTalentEngagementError' && el.code === 'RECENTLY_DECLINED') {
      throw new IndeedCandidateRecentlyDeclinedException(el.message);
    }
  });

  return parsedResponse;
};
